import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notification } from 'ui-library';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import useGetSecurities from 'hooks/useGetSecurities';
import {
    buildDocumentCategoryGroups,
    filterDocumentCategoryGroups,
    getBundleDocumentId,
    getDocumentCategoryOptions,
    getSecurityIdsFromCategories,
    matchesDocumentFilters,
    StaticCategoryDocument,
} from '../utils/adaptStaticDocumentCategories';
import { useStaticDocumentCategories } from './useStaticDocumentCategories';
import { getProcessOptions, PROCESS_ID_TO_VALUE, PROCESS_VALUE_TO_ID } from './useBundleDocuments';

const MODEL_PORTFOLIO_STALE_TIME = 1000 * 60 * 60; // 60 minutes

type ProductOption = { value: string; label: string };

const useUploadBundledDocuments = ({
    productOptions = [],
    processFilter,
    productFilter,
    documentCategory,
    onUploadSuccess,
}: {
    productOptions: ProductOption[];
    processFilter?: string | null;
    productFilter?: string | null;
    documentCategory?: string | null;
    onUploadSuccess?: () => void;
}) => {
    const { t, i18n: { language } } = useTranslation();
    const [isUploading, setIsUploading] = useState(false);

    const { categories, isLoading: isCategoriesLoading } = useStaticDocumentCategories();

    const securityIds = useMemo(() => getSecurityIdsFromCategories(categories), [categories]);

    const { data: securities, loading: isSecuritiesLoading } = useGetSecurities({
        loadInitially: securityIds.length > 0,
        ids: securityIds,
        take: securityIds?.length,
        options: { staleTime: MODEL_PORTFOLIO_STALE_TIME, gcTime: MODEL_PORTFOLIO_STALE_TIME },
    });

    const groups = useMemo(() => buildDocumentCategoryGroups(categories), [categories]);

    // The two filter dropdowns only narrow down the document category list.
    const documentCategoryOptions = useMemo(() => getDocumentCategoryOptions({
        groups: filterDocumentCategoryGroups({
            groups,
            productIds: productFilter ? [String(productFilter)] : [],
            processIds: processFilter ? [PROCESS_VALUE_TO_ID[processFilter]] : [],
        }),
        securities: securities as any,
    }), [groups, productFilter, processFilter, securities]);

    const selectedGroup = useMemo(
        () => groups.find((group) => group.value === documentCategory),
        [groups, documentCategory],
    );

    const selectedDocuments = useMemo<StaticCategoryDocument[]>(
        () => selectedGroup?.documents ?? [],
        [selectedGroup],
    );

    // Processes / products the selected document is actually part of.
    const processCheckboxOptions = useMemo(() => {
        const processValues = new Set(
            selectedDocuments.map((document) => PROCESS_ID_TO_VALUE[Number(document?.process)]).filter(Boolean),
        );

        return getProcessOptions({ t }).filter((option) => processValues.has(option.value));
    }, [selectedDocuments, t]);

    const productCheckboxOptions = useMemo(() => {
        const productIds = new Set(selectedDocuments.map((document) => String(document?.productId)));

        return productOptions.filter((option) => productIds.has(String(option.value)));
    }, [selectedDocuments, productOptions]);

    const selectedSecurity = useMemo(
        () => (securities as any[])?.find((item) => Number(item?.id) === Number(selectedGroup?.securityId)),
        [securities, selectedGroup?.securityId],
    );

    // BIB documents additionally reference a model portfolio, which is product scoped —
    // same resolution as the view details modal in useListOfBundledDocuments.
    const modelPortfolioProductId = useMemo(
        () => selectedDocuments.find((document) => document?.productId)?.productId,
        [selectedDocuments],
    );

    const { data: modelPortfolios, isLoading: isModelPortfoliosLoading } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getModelPortfolios',
        payload: [Number(productFilter) || modelPortfolioProductId, { language }],
        enabled: !!selectedGroup?.modelPortfolioId && !!(productFilter || modelPortfolioProductId),
        refetchOnWindowFocus: false,
        staleTime: MODEL_PORTFOLIO_STALE_TIME,
    });

    const selectedModelPortfolio = useMemo(
        () => (modelPortfolios?.data ?? []).find(
            (portfolio) => Number(portfolio?.Id) === Number(selectedGroup?.modelPortfolioId),
        ),
        [modelPortfolios?.data, selectedGroup?.modelPortfolioId],
    );

    const { mutateAsync: handleMutation } = useGenericAPIMutation();

    // Every bundle document of the selected category that matches the ticked processes/products
    // gets replaced by the uploaded files.
    const getBundleDocumentIds = useCallback(
        (processes: string[] = [], products: string[] = []) => selectedDocuments
            .filter((document) => matchesDocumentFilters(
                document,
                products.map(String),
                processes.map((value) => PROCESS_VALUE_TO_ID[value]),
            ))
            .map((document) => getBundleDocumentId(document))
            .filter(Boolean),
        [selectedDocuments],
    );

    const uploadDocuments = useCallback(async ({ processes, products, documents }) => {
        const files = Object.entries(documents ?? {})
            .filter(([, file]) => !!file)
            .map(([documentLanguage, file]) => ({ language: documentLanguage, file }));

        const bundleDocumentIds = getBundleDocumentIds(processes, products);

        if (!files.length || !bundleDocumentIds.length) {
            return;
        }

        setIsUploading(true);
        try {
            await handleMutation({
                service: 'customDocumentsService',
                API: 'uploadStaticDocumentsInABundle',
                payload: { files, bundleDocumentIds, language },
                invalidateQueryKeys: [
                    'getData_getAllStaticDocumentsInABundle',
                    'getData_getAllStaticDocumentCategories',
                    'getData_getBundledDocuments',
                ],
            });

            notification.open({
                content: t(
                    'advisoryDashboard.documents.uploadDocuments.uploadSuccess',
                    'Documents uploaded successfully',
                ),
                type: 'success',
            });

            onUploadSuccess?.();
        } catch (error) {
            // useGenericAPIMutation already surfaces the error notification.
        } finally {
            setIsUploading(false);
        }
    }, [getBundleDocumentIds, handleMutation, language, onUploadSuccess, t]);

    return {
        documentCategoryOptions,
        processFilterOptions: getProcessOptions({ t }),
        processCheckboxOptions,
        productCheckboxOptions,
        selectedGroup,
        selectedSecurity,
        selectedModelPortfolio,
        hasBundleDocuments: selectedDocuments.length > 0,
        isCategoriesLoading,
        isModelPortfoliosLoading,
        isSecuritiesLoading,
        isUploading,
        uploadDocuments,
    };
};

export default useUploadBundledDocuments;
